import { useRef, useState, useMemo } from "react"
import {
    Box,
    Button,
    DialogActions,
    DialogContent,
    DialogTitle,
    LinearProgress,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material"
import * as XLSX from "xlsx"
import { Repeat } from "lib/@components/repeat"
import { If } from "lib/@components/switch"
import { invalidate } from "lib/graphql/cache"
import { successSnackbar } from "lib/snackbar/success-snackbar"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { createFentraUser } from "routes/qa/fentra/controllers/fentra-mutations"
import { FentraImportRow } from "routes/qa/fentra/users/fentra-import-row"
import { FentraImportErrorRow } from "routes/qa/fentra/users/fentra-import-error-row"

const STEP_PICK = "pick"
const STEP_PREVIEW = "preview"
const STEP_PROGRESS = "progress"
const STEP_DONE = "done"

export function FentraImportDialog({ profileId, cancel }) {
    const [step, setStep] = useState(STEP_PICK)
    const [rows, setRows] = useState([])
    const [progress, setProgress] = useState(0)
    const [results, setResults] = useState(null)
    const fileInputRef = useRef(null)

    const validRows = useMemo(getValidRows, [rows])
    const invalidRows = useMemo(getInvalidRows, [rows])

    return (
        <>
            <DialogTitle>Import Users from Excel</DialogTitle>

            <If value={step === STEP_PICK}>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        Select an <strong>.xlsx</strong> or <strong>.csv</strong> file. Required column:{" "}
                        <code>email</code>. Optional columns: <code>displayName</code>, <code>givenName</code> (or{" "}
                        <code>firstName</code>), <code>familyName</code> (or <code>lastName</code>),{" "}
                        <code>jobTitle</code>, <code>userName</code>.
                    </Typography>
                    <Button variant="outlined" component="label">
                        Choose File
                        <input ref={fileInputRef} type="file" accept=".xlsx,.csv" hidden onChange={handleFileChange} />
                    </Button>
                </DialogContent>
                <DialogActions>
                    <Button onClick={cancel}>Cancel</Button>
                </DialogActions>
            </If>

            <If value={step === STEP_PREVIEW}>
                <DialogContent>
                    <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                        <Typography variant="body2">
                            <strong>{validRows.length}</strong> valid
                        </Typography>
                        <If value={invalidRows.length > 0}>
                            <Typography variant="body2" color="error">
                                <strong>{invalidRows.length}</strong> invalid (highlighted below)
                            </Typography>
                        </If>
                    </Stack>
                    <Box sx={{ maxHeight: 400, overflow: "auto" }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ width: 32 }} />
                                    <TableCell>Email</TableCell>
                                    <TableCell>Display Name</TableCell>
                                    <TableCell>Given Name</TableCell>
                                    <TableCell>Family Name</TableCell>
                                    <TableCell>Job Title</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <Repeat list={rows} item={<FentraImportRow />} keyFn={rowKeyFn} />
                            </TableBody>
                        </Table>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleBack}>Back</Button>
                    <Button variant="contained" onClick={handleImport} disabled={validRows.length === 0}>
                        Import {validRows.length} user{validRows.length !== 1 ? "s" : ""}
                    </Button>
                </DialogActions>
            </If>

            <If value={step === STEP_PROGRESS}>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        Importing… {progress} / {validRows.length}
                    </Typography>
                    <LinearProgress
                        variant="determinate"
                        value={validRows.length > 0 ? (progress / validRows.length) * 100 : 0}
                    />
                </DialogContent>
                <DialogActions>
                    <Button disabled>Please wait…</Button>
                </DialogActions>
            </If>

            <If value={step === STEP_DONE}>
                <DialogContent>
                    <If value={results !== null}>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            <strong>{results?.created}</strong> created, <strong>{results?.failed}</strong> failed.
                        </Typography>
                        <If value={results?.errors?.length > 0}>
                            <Box sx={{ maxHeight: 300, overflow: "auto" }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Email</TableCell>
                                            <TableCell>Error</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        <Repeat
                                            list={results?.errors ?? []}
                                            item={<FentraImportErrorRow />}
                                            keyFn={errorKeyFn}
                                        />
                                    </TableBody>
                                </Table>
                            </Box>
                        </If>
                    </If>
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" onClick={cancel}>
                        Close
                    </Button>
                </DialogActions>
            </If>
        </>
    )

    function getValidRows() {
        return rows.filter((r) => !r._error)
    }

    function getInvalidRows() {
        return rows.filter((r) => r._error)
    }

    function rowKeyFn(r, i) {
        return r.email || i
    }

    function errorKeyFn(e, i) {
        return e.email || i
    }

    function handleBack() {
        setStep(STEP_PICK)
        setRows([])
    }

    async function handleFileChange(e) {
        const file = e.target.files?.[0]
        if (!file) return
        try {
            const arrayBuffer = await file.arrayBuffer()
            const wb = XLSX.read(new Uint8Array(arrayBuffer), { type: "array" })
            const sheet = wb.Sheets[wb.SheetNames[0]]
            const raw = XLSX.utils.sheet_to_json(sheet, { defval: "" })
            const parsed = parseRows(raw)
            setRows(parsed)
            setStep(STEP_PREVIEW)
        } catch (err) {
            errorSnackbar(err?.message ?? "Failed to parse file")
        }
        // reset so the same file can be re-selected
        if (fileInputRef.current) fileInputRef.current.value = ""
    }

    async function handleImport() {
        setProgress(0)
        setStep(STEP_PROGRESS)

        let created = 0
        let failed = 0
        const errors = []

        for (let i = 0; i < validRows.length; i++) {
            const row = validRows[i]
            try {
                const result = await createFentraUser({ profileId, ...row })
                if (result?.ok) {
                    created++
                } else {
                    failed++
                    const msg = result?.scimStatus
                        ? `SCIM ${result.scimStatus}: ${result.error}`
                        : result?.error ?? "Unknown error"
                    errors.push({ email: row.email, message: msg })
                }
            } catch (err) {
                failed++
                errors.push({ email: row.email, message: err?.message ?? "Unknown error" })
            }
            setProgress(i + 1)
        }

        if (created > 0) {
            invalidate("fentraUsers")
            successSnackbar(`${created} user${created !== 1 ? "s" : ""} imported`)
        }

        setResults({ created, failed, errors })
        setStep(STEP_DONE)
    }
}

function parseRows(raw) {
    const seen = new Set()
    return raw.map(parseRow)

    function parseRow(r) {
        const email = col(r, "email", "emailaddress", "email address", "e-mail")
        const displayName = col(r, "displayname", "display name", "display_name")
        const givenName = col(r, "givenname", "given name", "given_name", "firstname", "first name", "first_name")
        const familyName = col(r, "familyname", "family name", "family_name", "lastname", "last name", "last_name")
        const jobTitle = col(r, "jobtitle", "job title", "job_title")
        const userName = col(r, "username", "user name", "user_name") || email

        const row = { email, displayName, givenName, familyName, jobTitle, userName }

        if (!email) {
            row._error = "Missing email"
        } else if (seen.has(email.toLowerCase())) {
            row._error = `Duplicate email: ${email}`
        } else {
            seen.add(email.toLowerCase())
        }

        return row
    }
}

function col(r, ...keys) {
    for (const k of keys) {
        const lower = k.toLowerCase()
        for (const rk of Object.keys(r)) {
            if (rk.toLowerCase() === lower) return String(r[rk]).trim()
        }
    }
    return ""
}
